import React, {ReactElement, useEffect, useState} from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText
} from '@material-ui/core';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {useForm, Controller} from 'react-hook-form';
import {useSnackbar} from 'notistack';
import InsuranceCardImages from '../../../Patients/Patientdetails/EligibilityVerification/InsuranceCardImages/InsuranceCardImages';
import KeyValueCard from '@components/system/KeyValueCard/KeyValueCard';
import StyledOutlinedInput from '@components/system/FormInput/StyledOutlinedInput';
import {updatePatientInsurance} from '@shared/services/InsuranceEligibilityServices';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import ButtonLoader from '@components/system/ButtonLoader/ButtonLoader';
import './ViewInsuranceImagesDialog.scss';

interface IViewInsuranceCardsDialogProps {
  isOpen: boolean;
  handleClose: () => void;
  selectedInsuranceData: any;
  fromEdit?: boolean;
  onUpdateSuccess?: () => void;
}

const ViewInsuranceImagesCardsDialog = (
  Props: IViewInsuranceCardsDialogProps
): ReactElement => {
  const {
    isOpen,
    handleClose,
    selectedInsuranceData,
    fromEdit = false,
    onUpdateSuccess
  } = Props;
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const patientDetailsDrawer = useSelector(
    (state: any) => state.patientDetailsDrawer
  );

  const {control, handleSubmit, errors, reset} = useForm({
    mode: 'all',
    defaultValues: {
      payer_name: selectedInsuranceData?.payer_name || '',
      subscriber_group_no: selectedInsuranceData?.subscriber_group_no || '',
      subscriber_member_no: selectedInsuranceData?.subscriber_member_no || ''
    }
  });

  useEffect(() => {
    if (isOpen && selectedInsuranceData) {
      reset({
        payer_name: selectedInsuranceData?.payer_name || '',
        subscriber_group_no: selectedInsuranceData?.subscriber_group_no || '',
        subscriber_member_no: selectedInsuranceData?.subscriber_member_no || ''
      });
    }
  }, [isOpen, selectedInsuranceData, reset]);

  // Prepare insurance data to pass to InsuranceCardImages
  const insurances = [
    {
      file_id1: selectedInsuranceData?.fileId1,
      file_id2: selectedInsuranceData?.fileId2,
      created_at: selectedInsuranceData?.last_verified_on
    }
  ];

  const insuranceData = [
    {
      label: t('ELIGIBILITY_VERIFICATION.PAYER_NAME'),
      value: selectedInsuranceData?.payer_name
        ? selectedInsuranceData?.payer_name
        : '-'
    },
    {
      label: t('ELIGIBILITY_VERIFICATION.SUBSCRIBER_GROUP_NUMBER'),
      value: selectedInsuranceData?.subscriber_group_no
        ? selectedInsuranceData?.subscriber_group_no
        : '-'
    },
    {
      label: t('ELIGIBILITY_VERIFICATION.SUBSCRIBER_MEMBER_NUMBER'),
      value: selectedInsuranceData?.subscriber_member_no
        ? selectedInsuranceData?.subscriber_member_no
        : '-'
    },
    {
      label: t('ELIGIBILITY_VERIFICATION.VERIFY_INSURANCE_FORM.BIN_NUMBER'),
      value: selectedInsuranceData?.bin_number
        ? selectedInsuranceData?.bin_number
        : '-'
    },
    {
      label: t(
        'ELIGIBILITY_VERIFICATION.VERIFY_INSURANCE_FORM.PROCESS_CONTROL_NUMBER'
      ),
      value: selectedInsuranceData?.process_control_number
        ? selectedInsuranceData?.process_control_number
        : '-'
    },
    {
      label: t(
        'ELIGIBILITY_VERIFICATION.VERIFY_INSURANCE_FORM.RX_GROUP_NUMBER'
      ),
      value: selectedInsuranceData?.rx_group_no
        ? selectedInsuranceData?.rx_group_no
        : '-'
    }
  ];

  const onSubmit = async (data: any) => {
    try {
      setIsSubmitting(true);
      await updatePatientInsurance(
        patientDetailsDrawer?.groupId,
        patientDetailsDrawer?.patientDetails?.patient_id,
        selectedInsuranceData?.id,
        {
          payer_name: data.payer_name,
          subscriber_group_no: data.subscriber_group_no,
          subscriber_member_no: data.subscriber_member_no
        }
      );
      enqueueSnackbar(
        t('PATIENT_DETAILS_DRAWER.INSURANCE_MODULE.UPDATE_SUCCESS'),
        SnackBarConfig('s')
      );
      if (fromEdit && onUpdateSuccess) {
        onUpdateSuccess();
      }
      handleClose();
    } catch (error: any) {
      enqueueSnackbar(
        (error && error.message) ||
          t('PATIENT_DETAILS_DRAWER.INSURANCE_MODULE.UPDATE_ERROR'),
        SnackBarConfig('e')
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    reset({
      payer_name: selectedInsuranceData?.payer_name || '',
      subscriber_group_no: selectedInsuranceData?.subscriber_group_no || '',
      subscriber_member_no: selectedInsuranceData?.subscriber_member_no || ''
    });
    handleClose();
  };

  const renderEditForm = () => (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="insurance-edit-form-wrapper">
        <Controller
          name="payer_name"
          control={control}
          rules={{
            required: {
              value: true,
              message: 'PATIENT.INSURANCE.UPDATE.ERROR.PAYER_NAME_REQUIRED'
            }
          }}
          render={({onChange, value}) => (
            <div className="insurance-form-field">
              <p className="key">
                {t('ELIGIBILITY_VERIFICATION.PAYER_NAME')}
                <span className="required">*</span>
              </p>
              <p>:</p>
              <div className="value">
                <StyledOutlinedInput
                  type="text"
                  value={value}
                  onChange={(e: any) => onChange(e.target.value)}
                  error={!!errors.payer_name}
                  fullWidth
                />
                {errors.payer_name && (
                  <FormHelperText error>
                    {t(errors.payer_name.message)}
                  </FormHelperText>
                )}
              </div>
            </div>
          )}
        />
        <Controller
          name="subscriber_group_no"
          control={control}
          rules={{
            required: {
              value: true,
              message:
                'PATIENT.INSURANCE.UPDATE.ERROR.SUBSCRIBER_GROUP_NO_REQUIRED'
            }
          }}
          render={({onChange, value}) => (
            <div className="insurance-form-field">
              <p className="key">
                {t('ELIGIBILITY_VERIFICATION.SUBSCRIBER_GROUP_NUMBER')}
                <span className="required">*</span>
              </p>
              <p>:</p>
              <div className="value">
                <StyledOutlinedInput
                  type="text"
                  value={value}
                  onChange={(e: any) => onChange(e.target.value)}
                  error={!!errors.subscriber_group_no}
                  fullWidth
                />
                {errors.subscriber_group_no && (
                  <FormHelperText error>
                    {t(errors.subscriber_group_no.message)}
                  </FormHelperText>
                )}
              </div>
            </div>
          )}
        />
        <Controller
          name="subscriber_member_no"
          control={control}
          rules={{
            required: {
              value: true,
              message:
                'PATIENT.INSURANCE.UPDATE.ERROR.SUBSCRIBER_MEMBER_NO_REQUIRED'
            }
          }}
          render={({onChange, value}) => (
            <div className="insurance-form-field">
              <p className="key">
                {t('ELIGIBILITY_VERIFICATION.SUBSCRIBER_MEMBER_NUMBER')}
                <span className="required">*</span>
              </p>
              <p>:</p>
              <div className="value">
                <StyledOutlinedInput
                  type="text"
                  value={value}
                  onChange={(e: any) => onChange(e.target.value)}
                  error={!!errors.subscriber_member_no}
                  fullWidth
                />
                {errors.subscriber_member_no && (
                  <FormHelperText error>
                    {t(errors.subscriber_member_no.message)}
                  </FormHelperText>
                )}
              </div>
            </div>
          )}
        />
      </div>
    </form>
  );

  return (
    <Dialog
      open={isOpen}
      onClose={handleCancel}
      fullWidth={true}
      maxWidth={'md'}
      disableBackdropClick={true}
      disableEscapeKeyDown={true}>
      <DialogTitle disableTypography>
        {fromEdit
          ? t('PATIENT_DETAILS_DRAWER.INSURANCE_MODULE.EDIT_INSURANCE')
          : t('ELIGIBILITY_VERIFICATION.INSURANCE_CARD')}
      </DialogTitle>
      <DialogContent dividers>
        <div>
          {fromEdit ? renderEditForm() : <KeyValueCard data={insuranceData} />}
          {selectedInsuranceData?.fileId1 && selectedInsuranceData?.fileId2 && (
            <div style={{marginTop: '1rem'}}>
              <InsuranceCardImages
                clinicId={patientDetailsDrawer?.clinicId}
                groupId={patientDetailsDrawer?.groupId}
                patientId={patientDetailsDrawer?.patientDetails?.patient_id}
                insurances={insurances} // Pass the selected insurance as an array
                showTitle={false}
              />
            </div>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        {fromEdit ? (
          <>
            <Button
              type="button"
              onClick={handleCancel}
              color="primary"
              disabled={isSubmitting}>
              {t('COMMON.CANCEL')}
            </Button>
            <Button
              onClick={handleSubmit(onSubmit)}
              color="primary"
              variant="contained"
              disabled={isSubmitting}>
              {isSubmitting ? (
                <ButtonLoader />
              ) : (
                t('PATIENT_DETAILS_DRAWER.INSURANCE_MODULE.UPDATE')
              )}
            </Button>
          </>
        ) : (
          <Button
            type="button"
            onClick={handleCancel}
            color="primary"
            variant="contained">
            {t('COMMON.CLOSE')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ViewInsuranceImagesCardsDialog;
